import { useEffect, useRef, useState } from 'react';

export interface ExportMenuItem {
  label: string;
  icon?: string;
  hint?: string;
  onSelect: () => void | Promise<void>;
  disabled?: boolean;
}

/**
 * 统一导出入口下拉：一个「⬇ 导出」按钮聚合同一视图的全部导出方式
 * （xlsx 数据表 / A4 套打打印版），避免工具栏并排多个导出按钮。
 */
export default function ExportMenu({
  items,
  busy,
  size = 'sm',
  className = '',
}: {
  items: ExportMenuItem[];
  busy?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 外点 / Esc 收起
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        title="导出：Excel 数据表 / A4 套打打印版"
        className={`btn btn-accent ${size === 'sm' ? 'btn-sm' : ''} ${busy ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        {busy ? '处理中…' : '⬇ 导出 ▾'}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-40">
          {items.map((it) => (
            <button
              key={it.label}
              disabled={it.disabled || busy}
              title={it.hint}
              onClick={() => {
                setOpen(false);
                void it.onSelect();
              }}
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
            >
              <span className="mr-1.5">{it.icon || '⬇'}</span>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
